import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Hash, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Sales", "Purchase", "Credit Note", "Debit Note"];

const AccountsInvoiceSettings = () => {
  const [settings, setSettings] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "Sales", year: "", prefix: "", suffix: "", starting_number: "1", padding: "3" });

  const fetch = useCallback(async () => {
    const r = await axios.get(`${API}/accounts/invoice-settings`);
    setSettings(r.data);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.category || !form.year) { toast.error("Category and year are required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/accounts/invoice-settings/${editing.id}`, form);
        toast.success("Setting updated");
      } else {
        await axios.post(`${API}/accounts/invoice-settings`, form);
        toast.success("Setting created");
      }
      setShowDialog(false); setEditing(null);
      setForm({ category: "Sales", year: "", prefix: "", suffix: "", starting_number: "1", padding: "3" });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this setting?")) return;
    await axios.delete(`${API}/accounts/invoice-settings/${id}`);
    toast.success("Deleted"); fetch();
  };

  const previewInvoice = (s) => {
    const num = String(s.current_number || s.starting_number || 1).padStart(s.padding || 3, "0");
    return `${s.prefix || ""}${num}${s.suffix || ""}`;
  };

  const catColor = (c) => ({ "Sales": "text-green-400 bg-green-900/30", "Purchase": "text-blue-400 bg-blue-900/30", "Credit Note": "text-amber-400 bg-amber-900/30", "Debit Note": "text-red-400 bg-red-900/30" }[c] || "text-gray-400");

  return (
    <div className="space-y-4" data-testid="invoice-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><Hash className="w-5 h-5" /> Invoice Number Settings</h2>
          <p className="text-gray-400 text-sm">Configure prefix, suffix & starting number per category per year</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ category: "Sales", year: "", prefix: "", suffix: "", starting_number: "1", padding: "3" }); setShowDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700" data-testid="add-invoice-setting-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Setting
        </Button>
      </div>

      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Category</TableHead>
                <TableHead className="text-gray-400">Year</TableHead>
                <TableHead className="text-gray-400">Prefix</TableHead>
                <TableHead className="text-gray-400">Suffix</TableHead>
                <TableHead className="text-gray-400">Start #</TableHead>
                <TableHead className="text-gray-400">Current #</TableHead>
                <TableHead className="text-gray-400">Preview</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">No settings yet. Click "Add Setting" to configure invoice numbers.</TableCell></TableRow>
              ) : settings.map(s => (
                <TableRow key={s.id} className="border-gray-800">
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${catColor(s.category)}`}>{s.category}</span></TableCell>
                  <TableCell className="text-cyan-400 font-mono">{s.year}</TableCell>
                  <TableCell className="text-gray-300 font-mono">{s.prefix || "—"}</TableCell>
                  <TableCell className="text-gray-300 font-mono">{s.suffix || "—"}</TableCell>
                  <TableCell className="text-gray-300">{s.starting_number}</TableCell>
                  <TableCell className="text-emerald-400 font-bold">{s.current_number}</TableCell>
                  <TableCell className="text-white font-mono font-bold">{previewInvoice(s)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2"
                      onClick={() => { setEditing(s); setForm({ category: s.category, year: s.year, prefix: s.prefix || "", suffix: s.suffix || "", starting_number: String(s.starting_number), padding: String(s.padding || 3) }); setShowDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader><DialogTitle className="text-blue-400">{editing ? "Edit" : "Add"} Invoice Setting</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})} disabled={!!editing}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Year</Label>
                <Input value={form.year} onChange={e => setForm({...form, year: e.target.value})} placeholder="e.g., 25-26" className="bg-gray-800 border-gray-600 text-white mt-1" disabled={!!editing} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Prefix</Label>
                <Input value={form.prefix} onChange={e => setForm({...form, prefix: e.target.value})} placeholder="e.g., CPI/" className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Suffix</Label>
                <Input value={form.suffix} onChange={e => setForm({...form, suffix: e.target.value})} placeholder="e.g., /25-26" className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Starting Number</Label>
                <Input type="number" value={form.starting_number} onChange={e => setForm({...form, starting_number: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Padding (digits)</Label>
                <Input type="number" value={form.padding} onChange={e => setForm({...form, padding: e.target.value})} placeholder="3" className="bg-gray-800 border-gray-600 text-white mt-1" />
              </div>
            </div>
            {/* Preview */}
            <div className="bg-gray-800/60 rounded-lg p-3 text-center">
              <span className="text-gray-400 text-sm">Preview: </span>
              <span className="text-emerald-400 font-mono font-bold text-lg">
                {form.prefix}{String(form.starting_number || "1").padStart(parseInt(form.padding) || 3, "0")}{form.suffix}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsInvoiceSettings;
